import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  FaSave, FaArrowLeft, FaPlus, FaTrash, FaSpinner, FaPrint, FaCopy, FaSearch,
  FaGripVertical, FaPaperPlane, FaCheckCircle, FaBan, FaChevronDown, FaChevronUp,
} from 'react-icons/fa'
import { Card, Banner, Spinner, PageHeader, Toast, useToast, Modal } from '../components/ui'
import { InvoicingPreviewNotice } from '../components/Notices'
import Combobox from '../components/Combobox'
import InvoiceDocument, { SheetPreview } from '../components/InvoiceDocument'
import { money } from '../lib/format'
import {
  KINDS, KIND_ORDER, blankInvoice, blankItem, computeTotals, issuerFromSettings,
  settingsMap, addDays, cents,
} from '../lib/invoice'
import * as api from '../lib/api'
import { useAuth } from '../context/AuthContext'

/**
 * Make an invoice in as few keystrokes as possible.
 *
 * The whole design follows from one observation: almost everything on an invoice
 * is already in the database. The supplier's VAT number is on the supplier. The
 * price of a shirt is on the shirt. Retyping either is both slower and a chance
 * to get it wrong — and a wrong VAT number is the kind of wrong that gets an
 * invoice rejected weeks later by someone else's accounts department.
 *
 * So: pick a party, its details arrive. Pick a product, its description, code
 * and price arrive. Everything remains editable afterwards, because a real
 * invoice always has one line that is not quite like the others.
 *
 * The right-hand pane is the actual document, at true A4, scaled to fit. Not a
 * summary of it — the same component that prints. What you approve is what is
 * sent.
 */

const InvoiceEditorPage = () => {
  // Deleting a document needs is_manager; staff can raise and edit them.
  const { canManage } = useAuth()
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNew = !id
  const { toast, notify, notifyError, dismiss } = useToast()

  const [form, setForm] = useState(null)
  const [items, setItems] = useState([blankItem()])
  const [settings, setSettings] = useState({})
  const [suppliers, setSuppliers] = useState([])
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showParty, setShowParty] = useState(false)
  const [pickingRow, setPickingRow] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const dragFrom = useRef(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [settingRows, supplierRows, customerRows, productRows] = await Promise.all([
          api.getSettings().catch(() => []),
          api.getSuppliers().catch(() => []),
          api.getCustomers().catch(() => []),
          api.getProducts().catch(() => []),
        ])
        if (cancelled) return

        const map = settingsMap(settingRows)
        setSettings(map)
        setSuppliers(supplierRows ?? [])
        setCustomers(customerRows ?? [])
        setProducts(productRows ?? [])

        if (isNew) {
          const draft = blankInvoice(map)
          // /invoices/new?kind=quote — so "New quote" on the list page lands on a
          // quote rather than on an invoice the operator has to change.
          const kind = searchParams.get('kind')
          if (kind && KINDS[kind]) draft.kind = kind
          setForm(draft)
          setItems([blankItem()])
          // A brand new document has nobody on it, so the one thing that must
          // happen first is open by default.
          setShowParty(true)
        } else {
          const invoice = await api.getInvoice(id)
          if (cancelled) return
          if (!invoice) {
            setError(new Error('That document could not be found.'))
          } else {
            setForm(invoice)
            setItems(
              (invoice.items ?? []).map((item) => ({
                ...item,
                key: item.id,
                quantity: Number(item.quantity),
                unit_price: Number(item.unit_price),
              })),
            )
          }
        }
      } catch (err) {
        if (!cancelled) setError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  const issuer = useMemo(() => issuerFromSettings(settings), [settings])

  const totals = useMemo(
    () =>
      form
        ? computeTotals({
            items,
            discountType: form.discount_type,
            discountValue: form.discount_value,
            shippingAmount: form.shipping_amount,
            taxRate: form.tax_rate,
            pricesIncludeTax: form.prices_include_tax,
          })
        : null,
    [form, items],
  )

  // --- The party dropdown --------------------------------------------------
  const partyOptions = useMemo(() => {
    const supplierRows = suppliers
      .filter((s) => s.is_active)
      .map((s) => ({
        id: `sup-${s.id}`,
        group: 'Suppliers',
        label: s.name,
        hint: [s.contact_name, s.city, s.vat_number && `VAT ${s.vat_number}`].filter(Boolean).join(' · '),
        search: `${s.name} ${s.trading_as ?? ''} ${s.contact_name ?? ''} ${s.email ?? ''} ${s.city ?? ''} ${s.vat_number ?? ''}`,
        row: s,
        kind: 'supplier',
      }))

    const customerRows = customers.map((c) => ({
      id: `cus-${c.id}`,
      group: 'Customers',
      label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email,
      hint: [c.email, c.city].filter(Boolean).join(' · '),
      search: `${c.first_name ?? ''} ${c.last_name ?? ''} ${c.email ?? ''} ${c.phone ?? ''} ${c.city ?? ''}`,
      row: c,
      kind: 'customer',
    }))

    return [...supplierRows, ...customerRows]
  }, [suppliers, customers])

  /**
   * Copy a party's details onto the document.
   *
   * A copy, not a link — see the migration. The invoice must keep showing the
   * address it was sent to even after the supplier moves premises.
   */
  const applyParty = (option) => {
    if (option.kind === 'supplier') {
      const s = option.row
      setForm((f) => ({
        ...f,
        supplier_id: s.id,
        customer_id: null,
        bill_to_name: s.trading_as || s.name,
        bill_to_contact: s.contact_name ?? '',
        bill_to_email: s.email ?? '',
        bill_to_phone: s.phone ?? '',
        bill_to_vat_number: s.vat_number ?? '',
        bill_to_reg_number: s.registration_number ?? '',
        bill_to_address: [s.address_line1, s.address_line2].filter(Boolean).join('\n'),
        bill_to_city: s.city ?? '',
        bill_to_province: s.province ?? '',
        bill_to_postal_code: s.postal_code ?? '',
        bill_to_country: s.country ?? 'South Africa',
        // Their own terms win over the shop's default — the whole reason the
        // field is on the supplier is that they differ.
        due_date: addDays(f.issue_date, s.payment_terms_days ?? settings.invoice_due_days ?? 30),
        reference: f.reference || (s.account_number ? `Account ${s.account_number}` : ''),
      }))
    } else {
      const c = option.row
      setForm((f) => ({
        ...f,
        customer_id: c.id,
        supplier_id: null,
        bill_to_name: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email,
        bill_to_contact: '',
        bill_to_email: c.email ?? '',
        bill_to_phone: c.phone ?? '',
        bill_to_vat_number: '',
        bill_to_reg_number: '',
        bill_to_address: c.address ?? '',
        bill_to_city: c.city ?? '',
        bill_to_province: c.province ?? '',
        bill_to_postal_code: c.postal_code ?? '',
        bill_to_country: 'South Africa',
      }))
    }
    setShowParty(true)
  }

  const applyTypedParty = (text) => {
    setForm((f) => ({ ...f, supplier_id: null, customer_id: null, bill_to_name: text }))
    setShowParty(true)
  }

  // --- Lines ---------------------------------------------------------------
  const productOptions = useMemo(
    () =>
      products
        .filter((p) => p.status !== 'archived')
        .map((p) => ({
          id: p.id,
          label: p.name,
          hint: [p.sku, p.brand, p.category].filter(Boolean).join(' · '),
          trailing: money(p.price),
          search: `${p.name} ${p.sku ?? ''} ${p.brand ?? ''} ${p.category ?? ''}`,
          row: p,
        })),
    [products],
  )

  const lineFromProduct = (product) => ({
    ...blankItem(),
    product_id: product.id,
    description: product.name,
    sku: product.sku ?? '',
    quantity: 1,
    unit_price: Number(product.price ?? 0),
  })

  const addProductLine = (option) => {
    setItems((current) => {
      // A blank starter row is a placeholder, not a line the operator wrote.
      // Filling it is friendlier than leaving an empty row above the new one.
      const blankIndex = current.findIndex((item) => !item.description.trim())
      const line = lineFromProduct(option.row)
      if (blankIndex === -1) return [...current, line]
      const next = [...current]
      next[blankIndex] = line
      return next
    })
  }

  const updateItem = (index, patch) =>
    setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)))

  const removeItem = (index) =>
    setItems((current) => {
      const next = current.filter((_, i) => i !== index)
      return next.length > 0 ? next : [blankItem()]
    })

  const moveItem = (from, to) =>
    setItems((current) => {
      if (from === to || from == null || to == null) return current
      const next = [...current]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })

  // --- Saving --------------------------------------------------------------
  const filledItems = items.filter((item) => item.description.trim())

  const save = async ({ then } = {}) => {
    setSaving(true)
    setError(null)
    try {
      if (!form.bill_to_name?.trim()) {
        throw new Error('Choose or type who this document is made out to.')
      }
      if (filledItems.length === 0) {
        throw new Error('Add at least one line before saving.')
      }

      const saved = await api.saveInvoice(isNew ? null : id, form, filledItems)
      setForm(saved)
      setItems(
        (saved.items ?? []).map((item) => ({
          ...item,
          key: item.id,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
        })),
      )
      notify(`${saved.number} saved.`)
      if (isNew) navigate(`/invoices/${saved.id}`, { replace: true })
      then?.(saved)
    } catch (err) {
      setError(err)
      notifyError(err)
    } finally {
      setSaving(false)
    }
  }

  const changeStatus = async (status) => {
    setSaving(true)
    try {
      const updated = await api.setInvoiceStatus(id, status)
      setForm((f) => ({ ...f, ...updated }))
      notify(
        status === 'sent' ? `${form.number} marked as sent.`
        : status === 'paid' ? `${form.number} marked as paid in full.`
        : status === 'cancelled' ? `${form.number} cancelled.`
        : `${form.number} back to draft.`,
      )
    } catch (err) {
      notifyError(err)
    } finally {
      setSaving(false)
    }
  }

  const duplicate = async () => {
    setSaving(true)
    try {
      const copy = await api.duplicateInvoice(id)
      notify(`Copied to ${copy.number}.`)
      navigate(`/invoices/${copy.id}`)
    } catch (err) {
      notifyError(err)
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    setSaving(true)
    try {
      await api.deleteInvoice(id)
      notify('Document deleted.')
      navigate('/invoices', { replace: true })
    } catch (err) {
      notifyError(err)
      setSaving(false)
    }
  }

  /** Save first, then print — printing an unsaved draft prints a lie. */
  const printDocument = async () => {
    if (isNew) {
      await save({ then: (saved) => window.open(`/invoices/${saved.id}/print`, '_blank') })
    } else {
      await save({ then: () => window.open(`/invoices/${id}/print`, '_blank') })
    }
  }

  if (loading) return <Spinner label="Loading…" />
  if (error && !form) {
    return <Banner tone="error"><strong>Could not open this document.</strong> {error.message}</Banner>
  }
  if (!form) return null

  const meta = KINDS[form.kind] ?? KINDS.invoice
  const locked = form.status === 'cancelled'

  return (
    <div>
      <PageHeader
        title={isNew ? `New ${meta.label.toLowerCase()}` : form.number}
        subtitle={
          isNew
            ? 'It is numbered the moment you save it'
            : `${meta.label} · ${form.bill_to_name} · ${money(form.total)}`
        }
      >
        <Link to="/invoices" className="btn-ghost">
          <FaArrowLeft className="text-[11px]" />
          All documents
        </Link>
        {!isNew && (
          <button onClick={duplicate} disabled={saving} className="btn-ghost" title="Copy to a new draft">
            <FaCopy className="text-[11px]" />
            Duplicate
          </button>
        )}
        <button onClick={printDocument} disabled={saving} className="btn-ghost">
          <FaPrint className="text-[11px]" />
          Print / PDF
        </button>
        <button onClick={() => save()} disabled={saving || locked} className="btn-primary">
          {saving ? <FaSpinner className="animate-spin" /> : <FaSave className="text-[11px]" />}
          Save
        </button>
      </PageHeader>

      <InvoicingPreviewNotice />

      {api.isDemo && (
        <div className="mb-4">
          <Banner tone="info">
            Demo mode — the generator works and the preview is live, but saving is disabled until
            Supabase is connected.
          </Banner>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Banner tone="error" onDismiss={() => setError(null)}>{error.message}</Banner>
        </div>
      )}

      {locked && (
        <div className="mb-4">
          <Banner tone="warning">
            <strong>This document is cancelled.</strong> It is kept so the numbering has no hole in
            it. Put it back to draft to work on it again.
          </Banner>
        </div>
      )}

      {form.status === 'sent' && (
        <div className="mb-4">
          <Banner tone="warning">
            <strong>Already sent.</strong> Changing a document after it has gone out means the copy
            you hold no longer matches the copy they hold. For a correction, raise a credit note
            instead.
          </Banner>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-5">
        {/* ================= Form ================= */}
        <div className="space-y-4 xl:col-span-3">
          {/* ---- Kind ---- */}
          <Card title="What is this?">
            <div className="flex flex-wrap gap-1.5">
              {KIND_ORDER.map((kind) => (
                <button
                  key={kind}
                  onClick={() => set('kind', kind)}
                  disabled={!isNew}
                  className={`rounded-lg border px-3 py-1.5 text-2xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    form.kind === kind
                      ? 'border-brand-navy bg-brand-navy text-white'
                      : 'border-hairline text-ink-secondary hover:border-baseline hover:text-ink'
                  }`}
                >
                  {KINDS[kind].label}
                </button>
              ))}
            </div>
            <p className="field-hint">
              {meta.blurb}
              {!isNew && ' The type is fixed once a number has been issued — the prefix is part of it.'}
            </p>
          </Card>

          {/* ---- Who ---- */}
          <Card
            title={form.kind === 'credit_note' ? 'Credit to' : meta.owed ? 'Bill to' : 'Prepared for'}
            subtitle="Pick a supplier or a customer and their details fill in"
            action={
              form.bill_to_name ? (
                <button
                  onClick={() => setShowParty((v) => !v)}
                  className="btn-subtle btn-sm"
                  aria-expanded={showParty}
                >
                  {showParty ? <FaChevronUp className="text-[9px]" /> : <FaChevronDown className="text-[9px]" />}
                  {showParty ? 'Hide details' : 'Edit details'}
                </button>
              ) : null
            }
          >
            <Combobox
              options={partyOptions}
              value={form.bill_to_name || null}
              onSelect={applyParty}
              onFreeText={applyTypedParty}
              allowFreeText
              placeholder="Search suppliers and customers…"
              emptyLabel="No match — type a name to use it as it is"
              freeTextLabel={(text) => `Make it out to “${text}”`}
              ariaLabel="Choose who this document is for"
            />

            {form.supplier_id && (
              <p className="field-hint">
                From your supplier list. The details below are copied onto this document — changing
                the supplier later will not alter it.
              </p>
            )}
            {!form.supplier_id && !form.customer_id && form.bill_to_name && (
              <p className="field-hint">
                Typed in by hand.{' '}
                <Link to="/suppliers" className="font-medium text-brand-red hover:underline">
                  Add them to the supplier list
                </Link>{' '}
                to pick them next time.
              </p>
            )}

            {showParty && (
              <div className="mt-3.5 space-y-3 border-t border-hairline pt-3.5">
                <Field label="Name on the document" id="bill_to_name">
                  <input
                    id="bill_to_name"
                    className="input"
                    value={form.bill_to_name ?? ''}
                    onChange={(e) => set('bill_to_name', e.target.value)}
                    placeholder="Company or person"
                  />
                </Field>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Attention of" id="bill_to_contact">
                    <input
                      id="bill_to_contact"
                      className="input"
                      value={form.bill_to_contact ?? ''}
                      onChange={(e) => set('bill_to_contact', e.target.value)}
                      placeholder="Optional"
                    />
                  </Field>
                  <Field label="E-mail" id="bill_to_email">
                    <input
                      id="bill_to_email"
                      type="email"
                      className="input"
                      value={form.bill_to_email ?? ''}
                      onChange={(e) => set('bill_to_email', e.target.value)}
                    />
                  </Field>
                  <Field label="Telephone" id="bill_to_phone">
                    <input
                      id="bill_to_phone"
                      className="input"
                      value={form.bill_to_phone ?? ''}
                      onChange={(e) => set('bill_to_phone', e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Their VAT number"
                    id="bill_to_vat_number"
                    hint="Needed if they are going to claim the VAT back."
                  >
                    <input
                      id="bill_to_vat_number"
                      className="input"
                      value={form.bill_to_vat_number ?? ''}
                      onChange={(e) => set('bill_to_vat_number', e.target.value)}
                      placeholder="e.g. 4123456789"
                    />
                  </Field>
                </div>

                <Field label="Address" id="bill_to_address">
                  <textarea
                    id="bill_to_address"
                    rows={2}
                    className="input"
                    value={form.bill_to_address ?? ''}
                    onChange={(e) => set('bill_to_address', e.target.value)}
                    placeholder="Street and building"
                  />
                </Field>

                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Town" id="bill_to_city">
                    <input
                      id="bill_to_city"
                      className="input"
                      value={form.bill_to_city ?? ''}
                      onChange={(e) => set('bill_to_city', e.target.value)}
                    />
                  </Field>
                  <Field label="Province" id="bill_to_province">
                    <input
                      id="bill_to_province"
                      className="input"
                      value={form.bill_to_province ?? ''}
                      onChange={(e) => set('bill_to_province', e.target.value)}
                    />
                  </Field>
                  <Field label="Postal code" id="bill_to_postal_code">
                    <input
                      id="bill_to_postal_code"
                      className="input"
                      value={form.bill_to_postal_code ?? ''}
                      onChange={(e) => set('bill_to_postal_code', e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            )}
          </Card>

          {/* ---- Dates ---- */}
          <Card title="Dates and reference">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Date" id="issue_date">
                <input
                  id="issue_date"
                  type="date"
                  className="input"
                  value={form.issue_date ?? ''}
                  onChange={(e) => {
                    const next = e.target.value
                    setForm((f) => ({
                      ...f,
                      issue_date: next,
                      // Move the due date with it, keeping the same terms. An
                      // invoice back-dated to last month should not still be due
                      // thirty days from today.
                      due_date: next ? addDays(next, settings.invoice_due_days ?? 30) : f.due_date,
                    }))
                  }}
                />
              </Field>
              <Field
                label={form.kind === 'quote' ? 'Valid until' : 'Due'}
                id="due_date"
                hint={
                  form.due_date && form.issue_date
                    ? `${Math.round((new Date(form.due_date) - new Date(form.issue_date)) / 86400000)} days`
                    : undefined
                }
              >
                <input
                  id="due_date"
                  type="date"
                  className="input"
                  value={form.due_date ?? ''}
                  onChange={(e) => set('due_date', e.target.value)}
                />
              </Field>
              <Field
                label="Their reference"
                id="reference"
                hint="Their order number. Leaving it off is the usual reason an invoice sits unpaid."
              >
                <input
                  id="reference"
                  className="input"
                  value={form.reference ?? ''}
                  onChange={(e) => set('reference', e.target.value)}
                  placeholder="e.g. PO-4471"
                />
              </Field>
            </div>
          </Card>

          {/* ---- Lines ---- */}
          <Card
            title="Lines"
            subtitle="Pick from the catalogue, or type anything else"
            bodyClassName=""
          >
            <div className="border-b border-hairline p-3">
              <Combobox
                options={productOptions}
                value={null}
                onSelect={addProductLine}
                placeholder="Add a product — search by name, code or brand…"
                emptyLabel="No product matches. Use “Add a blank line” for anything else."
                ariaLabel="Add a product to this document"
              />
            </div>

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '38px' }} aria-label="Reorder" />
                    <th>Description</th>
                    <th className="num" style={{ width: '74px' }}>Qty</th>
                    <th style={{ width: '70px' }}>Unit</th>
                    <th className="num" style={{ width: '110px' }}>Price</th>
                    <th className="num" style={{ width: '110px' }}>Amount</th>
                    <th style={{ width: '38px' }} aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={item.key ?? item.id ?? index}
                      draggable
                      onDragStart={() => { dragFrom.current = index }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { moveItem(dragFrom.current, index); dragFrom.current = null }}
                    >
                      <td className="text-center">
                        <FaGripVertical
                          className="cursor-grab text-[10px] text-ink-muted/60"
                          aria-hidden="true"
                          title="Drag to reorder"
                        />
                      </td>

                      <td>
                        {pickingRow === index ? (
                          <Combobox
                            options={productOptions}
                            value={null}
                            onSelect={(option) => {
                              updateItem(index, {
                                product_id: option.row.id,
                                description: option.row.name,
                                sku: option.row.sku ?? '',
                                unit_price: Number(option.row.price ?? 0),
                              })
                              setPickingRow(null)
                            }}
                            onFreeText={(text) => {
                              updateItem(index, { description: text, product_id: null })
                              setPickingRow(null)
                            }}
                            allowFreeText
                            size="sm"
                            placeholder="Search the catalogue…"
                            ariaLabel={`Choose a product for line ${index + 1}`}
                          />
                        ) : (
                          <div className="flex items-start gap-1.5">
                            <button
                              onClick={() => setPickingRow(index)}
                              className="btn-subtle btn-sm mt-0.5 shrink-0 px-1.5"
                              title="Fill this line from the catalogue"
                              aria-label={`Fill line ${index + 1} from the catalogue`}
                            >
                              <FaSearch className="text-[9px]" />
                            </button>
                            <div className="min-w-0 flex-1 space-y-1">
                              <input
                                className="input input-sm"
                                value={item.description}
                                onChange={(e) => updateItem(index, { description: e.target.value })}
                                placeholder="What is being charged for"
                                aria-label={`Description for line ${index + 1}`}
                              />
                              <input
                                className="input input-sm text-ink-muted"
                                value={item.detail ?? ''}
                                onChange={(e) => updateItem(index, { detail: e.target.value })}
                                placeholder={item.sku ? `${item.sku} — add a size, colour or note` : 'Optional second line'}
                                aria-label={`Extra detail for line ${index + 1}`}
                              />
                            </div>
                          </div>
                        )}
                      </td>

                      <td>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          className="input input-sm text-right"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, { quantity: e.target.value })}
                          aria-label={`Quantity for line ${index + 1}`}
                        />
                      </td>

                      <td>
                        <input
                          className="input input-sm"
                          value={item.unit ?? ''}
                          onChange={(e) => updateItem(index, { unit: e.target.value })}
                          placeholder="ea"
                          aria-label={`Unit for line ${index + 1}`}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="input input-sm text-right"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, { unit_price: e.target.value })}
                          aria-label={`Unit price for line ${index + 1}`}
                        />
                      </td>

                      <td className="num font-medium">
                        {money(cents(Number(item.quantity || 0) * Number(item.unit_price || 0)))}
                      </td>

                      <td className="text-right">
                        <button
                          onClick={() => removeItem(index)}
                          className="btn-subtle btn-sm px-1.5 text-brand-red"
                          title="Remove this line"
                          aria-label={`Remove line ${index + 1}`}
                        >
                          <FaTrash className="text-[9px]" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-hairline p-3">
              <button onClick={() => setItems((c) => [...c, blankItem()])} className="btn-ghost btn-sm">
                <FaPlus className="text-[9px]" />
                Add a blank line
              </button>
              <div className="text-2xs text-ink-muted">
                {filledItems.length} line{filledItems.length === 1 ? '' : 's'} ·{' '}
                <strong className="text-ink">{money(totals.subtotal)}</strong>
              </div>
            </div>
          </Card>

          {/* ---- Money ---- */}
          <Card title="Discount, delivery and VAT">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Discount" id="discount_type">
                <div className="flex gap-2">
                  <select
                    id="discount_type"
                    className="input input-sm w-auto"
                    value={form.discount_type}
                    onChange={(e) => set('discount_type', e.target.value)}
                  >
                    <option value="none">None</option>
                    <option value="percent">%</option>
                    <option value="amount">R</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="input input-sm flex-1 text-right"
                    value={form.discount_value}
                    disabled={form.discount_type === 'none'}
                    onChange={(e) => set('discount_value', e.target.value === '' ? 0 : Number(e.target.value))}
                    aria-label="Discount amount"
                  />
                </div>
              </Field>

              <Field label="Delivery (R)" id="shipping_amount">
                <input
                  id="shipping_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  className="input text-right"
                  value={form.shipping_amount}
                  onChange={(e) => set('shipping_amount', e.target.value === '' ? 0 : Number(e.target.value))}
                />
              </Field>

              <Field label="VAT rate (%)" id="tax_rate">
                <input
                  id="tax_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="input text-right"
                  value={form.tax_rate}
                  onChange={(e) => set('tax_rate', e.target.value === '' ? 0 : Number(e.target.value))}
                />
              </Field>
            </div>

            <label className="mt-3 flex items-start gap-2.5 text-[13px]">
              <input
                type="checkbox"
                checked={form.prices_include_tax}
                onChange={(e) => set('prices_include_tax', e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-hairline text-brand-navy focus:ring-brand-navy/30"
              />
              <span>
                The prices above include VAT
                <span className="mt-0.5 block text-2xs text-ink-muted">
                  On for retail, where the shelf price is what the customer pays. Off for trade,
                  where VAT is added at the bottom. It changes the total, so check the preview.
                </span>
              </span>
            </label>

            <div className="mt-3.5 space-y-1 rounded-lg bg-plane p-3 text-[13px]">
              <Row label="Subtotal" value={money(totals.subtotal)} />
              {totals.discountAmount > 0 && (
                <Row label="Discount" value={`− ${money(totals.discountAmount)}`} />
              )}
              {totals.shippingAmount > 0 && <Row label="Delivery" value={money(totals.shippingAmount)} />}
              <Row
                label={form.prices_include_tax ? `VAT @ ${Number(form.tax_rate)}% (included)` : `VAT @ ${Number(form.tax_rate)}%`}
                value={money(totals.taxAmount)}
                muted
              />
              <div className="flex items-baseline justify-between border-t border-hairline pt-1.5">
                <span className="font-semibold">Total</span>
                <span className="text-base font-semibold tabular-nums">{money(totals.total)}</span>
              </div>
            </div>
          </Card>

          {/* ---- Words ---- */}
          <Card title="Notes and terms">
            <div className="space-y-3">
              <Field label="Notes on the document" id="notes" hint="Printed under the totals. The customer sees this.">
                <textarea
                  id="notes"
                  rows={2}
                  className="input"
                  value={form.notes ?? ''}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="e.g. Delivery to the Centurion branch, attention Marco."
                />
              </Field>
              <Field label="Terms" id="terms" hint="Printed at the foot. Pre-filled from Settings.">
                <textarea
                  id="terms"
                  rows={2}
                  className="input"
                  value={form.terms ?? ''}
                  onChange={(e) => set('terms', e.target.value)}
                />
              </Field>
              <Field label="Internal note" id="internal_notes" hint="Staff only. Never printed and never sent.">
                <textarea
                  id="internal_notes"
                  rows={2}
                  className="input"
                  value={form.internal_notes ?? ''}
                  onChange={(e) => set('internal_notes', e.target.value)}
                />
              </Field>
            </div>
          </Card>

          {!isNew && (
            <Card title="Status">
              <div className="flex flex-wrap gap-2">
                {form.status !== 'sent' && form.status !== 'paid' && (
                  <button onClick={() => changeStatus('sent')} disabled={saving} className="btn-primary btn-sm">
                    <FaPaperPlane className="text-[10px]" />
                    Mark as sent
                  </button>
                )}
                {form.status !== 'paid' && meta.owed && (
                  <button onClick={() => changeStatus('paid')} disabled={saving} className="btn-ghost btn-sm">
                    <FaCheckCircle className="text-[10px]" />
                    Mark as paid in full
                  </button>
                )}
                {form.status !== 'draft' && (
                  <button onClick={() => changeStatus('draft')} disabled={saving} className="btn-ghost btn-sm">
                    Back to draft
                  </button>
                )}
                {form.status !== 'cancelled' && (
                  <button onClick={() => changeStatus('cancelled')} disabled={saving} className="btn-ghost btn-sm text-brand-red">
                    <FaBan className="text-[10px]" />
                    Cancel this document
                  </button>
                )}
              </div>

              {meta.owed && form.status !== 'draft' && (
                <div className="mt-3.5 border-t border-hairline pt-3.5">
                  <Field
                    label="Paid so far (R)"
                    id="amount_paid"
                    hint="For a part payment or a deposit. The balance prints on the document."
                  >
                    <div className="flex gap-2">
                      <input
                        id="amount_paid"
                        type="number"
                        step="0.01"
                        min="0"
                        className="input text-right"
                        value={form.amount_paid ?? 0}
                        onChange={(e) => set('amount_paid', e.target.value === '' ? 0 : Number(e.target.value))}
                      />
                      <button
                        onClick={async () => {
                          try {
                            await api.recordInvoicePayment(id, Number(form.amount_paid || 0))
                            notify('Payment recorded.')
                          } catch (err) {
                            notifyError(err)
                          }
                        }}
                        className="btn-ghost"
                      >
                        Record
                      </button>
                    </div>
                  </Field>
                </div>
              )}

              {canManage && (
                <div className="mt-3.5 border-t border-hairline pt-3.5">
                  <p className="text-2xs text-ink-muted">
                    Deleting leaves a gap in the numbering that cannot be explained to a bookkeeper
                    later. Cancelling is almost always the right answer.
                  </p>
                  <button onClick={() => setConfirmDelete(true)} className="btn-ghost btn-sm mt-2 text-brand-red">
                    <FaTrash className="text-[9px]" />
                    Delete permanently
                  </button>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* ================= Preview ================= */}
        <div className="xl:col-span-2">
          <div className="xl:sticky xl:top-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">
                Preview
              </span>
              <span className="text-2xs text-ink-muted">Exactly what prints</span>
            </div>
            <SheetPreview>
              <InvoiceDocument invoice={form} items={filledItems} issuer={issuer} />
            </SheetPreview>
          </div>
        </div>
      </div>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${form.number}?`}
        footer={
          <>
            <button onClick={() => setConfirmDelete(false)} className="btn-ghost">Keep it</button>
            <button onClick={remove} disabled={saving} className="btn-danger">
              {saving && <FaSpinner className="animate-spin" />}
              Delete permanently
            </button>
          </>
        }
      >
        <Banner tone="warning">
          <strong>{form.number} will be gone for good</strong>, along with its {filledItems.length}{' '}
          line{filledItems.length === 1 ? '' : 's'}. The number is not reused, so the sequence will
          skip it — which is exactly the kind of gap an auditor asks about.
        </Banner>
        <p className="mt-3 text-[13px] text-ink-secondary">
          Cancelling keeps the record and marks it void, which is what accounting practice expects.
        </p>
      </Modal>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

const Field = ({ label, id, hint, children }) => (
  <div>
    <label className="label" htmlFor={id}>{label}</label>
    {children}
    {hint && <p className="field-hint">{hint}</p>}
  </div>
)

const Row = ({ label, value, muted }) => (
  <div className={`flex items-baseline justify-between ${muted ? 'text-ink-muted' : ''}`}>
    <span className="text-2xs">{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
)

export default InvoiceEditorPage
